// PageTemplate.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  updateNavigation,
  setSettingsNav,
  resetNav,
  initNav,
  settingNav,
} from "../../redux/navigationSlice";
import { setShowPopupFalse, updatenavPopup } from "../../redux/navPopupSlice";
import {
  updateAuthenticationByParam,
  fetchUserDataThunk,
  resetAuthentication,
} from "../../redux/authenticationSlice";

import SideBar from "../../components/SideBar/SideBar";
import Header from "../../components/Header/Header";
import PageTitle from "../../components/PageTitle/PageTitle";
import LoginPage from "../LoginPage/LoginPage";
import axios from "axios";
import { config } from "../../utils/config";
import { getCookie, setCookie } from "../../utils/cookieHandler";
import { useLocation, useNavigate } from "react-router-dom";
import {
  capitalizeFirstLetter,
  getNavigationDisplayName,
} from "../../utils/helperFuctions";

const PageTemplate = ({ children }) => {
  // redux
  const _navigation = useSelector((s) => s.navigation);
  const pageHeader = useSelector((s) => s.pageHeader);
  const navPopup = useSelector((s) => s.navPopup);
  const showFields = useSelector((s) => s.showFields);
  const authentication = useSelector((s) => s.authentication);
  const dispatch = useDispatch();

  const removeCookie = (name, domain) => {
    const base = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
    // No domain: remove for current host
    document.cookie = base;
    // Provided domain
    if (domain) {
      document.cookie = `${base}; domain=${domain}`;
    }
    // Try with current hostname explicitly
    if (typeof window !== "undefined" && window.location?.hostname) {
      document.cookie = `${base}; domain=${window.location.hostname}`;
      if (!window.location.hostname.startsWith(".")) {
        document.cookie = `${base}; domain=.${window.location.hostname}`;
      }
    }
  };

  const navigate = useNavigate();
  const signOutClick = () => {
    dispatch(resetAuthentication());
    removeCookie("_r");
    removeCookie("_AT");
    removeCookie("_RT");
    removeCookie("_n");
    removeCookie("directus_session_token");
    dispatch(setShowPopupFalse());
    navigate("/");
  };

  const userNavigation = [
    { name: "Sign out", href: "#", clickFunction: signOutClick },
  ];

  const navigation = _navigation.navigation;
  const settingsOpen = _navigation.isSettings ?? false;
  const settingsRoles = _navigation.settingsRolesAllowed;

  const [sidebarOpen, setSidebarOpen] = useState(false);

  // collapsed state (shared with SideBar)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("sidebarCollapsed") || "false");
    } catch {
      return false;
    }
  });
  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("sidebarCollapsed", JSON.stringify(next));
      return next;
    });
  };

  const loc = useLocation();

  // ---------- FIX: derive currentNav with useMemo (no setState in effect) ----------
  const currentNav = useMemo(() => {
    const path = loc.pathname;
    const pathname = path.slice(1);

    // Check all navigation items for current flags (original logic)
    for (const item of navigation) {
      if (item.subItems) {
        const sub = item.subItems.find((s) => s.current);
        if (sub && sub.href && sub.href !== "#") return sub;
      }
      if (item.name === "Dashboard" && pathname === "") return item;
      if (
        item.name?.toLowerCase() === pathname.toLowerCase() &&
        item.href &&
        item.href !== "#"
      ) {
        return item;
      }
    }

    // Fallback: use the navigation display name mapping instead of capitalizeFirstLetter
    const allNavigationArrays = [initNav, settingNav];
    const displayName = getNavigationDisplayName(
      path,
      navigation,
      allNavigationArrays
    );
    return { name: displayName };
  }, [loc.pathname, navigation]);
  // ---------------------------------------------------------------------------------

  const updateFlag = (name, flag, isSubItem) => {
    let changedCurrent = false;
    const updatedNav = navigation.map((item) => {
      if (item.subItems && isSubItem) {
        const subs = item.subItems.map((s) =>
          s.name === name ? { ...s, current: flag } : { ...s, current: false }
        );
        return { ...item, subItems: subs };
      } else if (!isSubItem && item.name === name) {
        changedCurrent = item.href && item.href !== "#";
        return {
          ...item,
          current: changedCurrent ? true : item.current,
          open: changedCurrent ? false : !item.open,
        };
      }
      return {
        ...item,
        current: changedCurrent ? false : item.current,
        open: item.open === true || item.current === true,
      };
    });

    return updatedNav.map((i) =>
      i.subItems
        ? { ...i, subItems: i.subItems.map((s) => ({ ...s, current: false })) }
        : i
    );
  };

  const changeCurrent = (e) => {
    // Robustly derive the clicked button and its name so clicks on spans/SVGs work
    let targetButton = e.target?.closest && e.target.closest("button");
    let name = targetButton?.name || e.target?.name;
    if (!name && e.currentTarget?.querySelector) {
      const fallbackBtn = e.currentTarget.querySelector("button[name]");
      name = fallbackBtn?.name;
      if (!targetButton) targetButton = fallbackBtn;
    }
    let isSettings = name === "settingsButton";

    if (navPopup.trigger) {
      if (name && name !== "settingsButton") {
        const newNav = updateFlag(
          name,
          true,
          e.target.closest("ul")?.classList?.contains("sub-item")
        );
        let hasHREF = newNav.find((x) => x.name === name);
        if (!hasHREF) {
          for (const pn of newNav) {
            if (pn.subItems) {
              const findSN = pn.subItems.find((x) => x.name === name);
              if (findSN) {
                hasHREF = findSN;
                break;
              }
            }
          }
        }
        if (hasHREF?.subItems)
          hasHREF = hasHREF.subItems.find((x) => x.current === true)?.href;
        else hasHREF = hasHREF.href;

        if (hasHREF && hasHREF !== "#") {
          if (
            Array.isArray(showFields) &&
            showFields.findIndex((x) => x.changed === true) !== -1
          ) {
            dispatch(
              updatenavPopup({ showPopup: true, isSettings, name, newNav })
            );
          } else {
            dispatch(updatenavPopup({ navigate: true, navLink: hasHREF }));
          }
        } else {
          dispatch(updateNavigation({ navigation: newNav }));
        }
      } else if (isSettings) {
        if (settingsOpen) dispatch(resetNav());
        else dispatch(setSettingsNav());
      }
    } else {
      if (name && name !== "settingsButton") {
        const newNav = updateFlag(
          name,
          true,
          e.target.closest("ul")?.classList?.contains("sub-item")
        );
        dispatch(updateNavigation({ navigation: newNav }));
      }
      if (isSettings) {
        if (settingsOpen) dispatch(resetNav());
        else dispatch(setSettingsNav());
      }
    }
  };

  // Harden auth effect to depend only on the token value it uses
  useEffect(() => {
    if (!authentication?.token) {
      const _AT = getCookie("_AT");
      if (_AT) {
        dispatch(
          updateAuthenticationByParam({ paramName: "token", value: _AT })
        );
        dispatch(fetchUserDataThunk());
      } else if (authentication?.token === undefined) {
        dispatch(
          updateAuthenticationByParam({ paramName: "token", value: "" })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication?.token]);

  const onLoginSubmit = async (email, password) => {
    try {
      const resp = await axios.post(
        `${config.apiUrl}/api/authentication/login`,
        { email, password },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getCookie("_AT")}`,
          },
        }
      );
      if (resp.data.token) {
        setCookie("_AT", resp.data.token);
        if (resp.data.refresh_token) {
          setCookie("_RT", resp.data.refresh_token);
        }
        dispatch(
          updateAuthenticationByParam({
            paramName: "token",
            value: resp.data.token,
          })
        );
        dispatch(fetchUserDataThunk());
      }
      const isCaptain = resp.data.userRole === process.env.REACT_APP_CAPTAIN_ID;
      const isOperator =
        resp.data.userRole === process.env.REACT_APP_OPERATOR_ROLE;
      const isPartner =
        resp.data.userRole === process.env.REACT_APP_PARTNER_ROLE;
      const isClient = resp.data.userRole === process.env.REACT_APP_CLIENT_ROLE;

      if (
        isCaptain === true ||
        isOperator === true ||
        isPartner === true ||
        isClient === true
      )
        navigate("/reservations");
      return { success: !!resp.data.token, data: resp.data };
    } catch (error) {
      console.error(error.message);
      return { success: false, error: error.message };
    }
  };

  return (
    <>
      {authentication?.token && (
        <>
          <SideBar
            isOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            navigation={navigation}
            clickFunction={changeCurrent}
            settingsOpened={settingsOpen}
            settingsRoles={settingsRoles}
            collapsed={sidebarCollapsed}
            onToggleCollapse={toggleSidebarCollapsed}
          />

          {/* CONTENT SHELL */}
          <div
            className={`h-screen ${
              sidebarCollapsed ? "lg:pl-20" : "lg:pl-72"
            } min-w-0`}
          >
            {/* Single scroll container that also contains sticky elements */}
            <div className="h-full overflow-y-auto overflow-x-hidden">
              {/* Header stays sticky at top of this scroll container */}
              <Header
                setSidebarOpen={setSidebarOpen}
                userNavigation={userNavigation}
                authentication={authentication}
              />
              {/* Page title sticks right under header */}
              <PageTitle
                title={currentNav?.name != null ? currentNav?.name : currentNav}
                showFilter={currentNav?.showFilter}
                addButton={pageHeader.addButton}
                buttonText={pageHeader.buttonText}
                buttonFunction={pageHeader.buttonFunction}
                socondaryButton={pageHeader.secondaryButton}
                secondarybuttonText={pageHeader.secondaryButtonText}
                secondarybuttonFunction={pageHeader.secondaryButtonFunction}
                sequenceButton={pageHeader.sequenceButton}
                sequenceButtonProps={pageHeader.sequenceButtonProps}
                billingCycleToggle={pageHeader.billingCycleToggle}
                billingCycleValue={pageHeader.billingCycleValue}
                billingCycleEnabled={pageHeader.billingCycleEnabled}
                onBillingCycleChange={pageHeader.onBillingCycleChange}
              />
              {/* Scrollable content area */}
              <main className="py-8">
                <div className="px-4 sm:px-6 lg:px-8">{children}</div>
              </main>
            </div>
          </div>
        </>
      )}
      {authentication?.token === "" && <LoginPage onSubmit={onLoginSubmit} />}
    </>
  );
};

export default PageTemplate;
